import { useLazyContext } from "@redotech/react-util/context";
import { useSearch } from "@redotech/react-util/search";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { useEnabledIntegrations } from "@redotech/redo-merchant-app-common/hooks/use-enabled-integrations";
import { useIntegrationsByTeam } from "@redotech/redo-merchant-app-common/hooks/use-integrations-by-team";
import { useTeamMarketingProviders } from "@redotech/redo-merchant-app-common/hooks/use-team-marketing-providers";
import {
  ConditionExpression,
  ConditionStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { SchemaBooleanExpression } from "@redotech/redo-model/advanced-flow/expressions/index";
import {
  ConditionDataSource,
  conditionDataSourceToFriendly,
} from "@redotech/redo-model/advanced-flow/steps";
import { Category } from "@redotech/redo-model/advanced-flow/triggers";
import { GenericDataType } from "@redotech/redo-model/advanced-flow/type-system/data-types/generic-types";
import { Schema } from "@redotech/redo-model/advanced-flow/type-system/schema";
import { MinimalCustomerGroup } from "@redotech/redo-model/customers/customer-group-definition";
import { SegmentConditionBlock as SegmentConditionBlockType } from "@redotech/redo-model/marketing/segments/segment-types";
import { ConjunctionMode } from "@redotech/redo-model/segments/segment-types";
import {
  BadgeRedoListItem,
  RedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import * as inspectorStyles from "@redotech/redo-web/inspector.module.css";
import {
  SegmentBuilderLocationContext,
  SegmentConditionBlock,
  SegmentEditorLocation,
} from "@redotech/redo-web/marketing/segments/segment-builder-editor";
import { isSegmentConditionBlockValid } from "@redotech/redo-web/marketing/segments/segment-in-progress-validator";
import { providersToDataSources } from "@redotech/redo-web/marketing/segments/util/provider-to-data-source";
import { Text } from "@redotech/redo-web/text";
import { camelToTitleCase } from "@redotech/string/transform";
import { filterTruthy } from "@redotech/util/array";
import { assertNever, DeepPartial } from "@redotech/util/type";
import Fuse from "fuse.js";
import { memo, useMemo, useState } from "react";
import { SegmentsContext } from "../../../../services/marketing/segments-service";
import {
  FrontendStepType,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "../steps";
import { useDelayedAutoFocus } from "./common/use-delayed-auto-focus";
import { FieldInspector } from "./inspectors";

/**
 * Helper to check if a checkout context predicate has the required values
 * based on its predicate type.
 */
function checkoutContextPredicateHasValues(
  expression: DeepPartial<SchemaBooleanExpression>,
): boolean {
  const predicateType = (expression as { predicateType?: string })
    .predicateType;
  if (predicateType === "collection_membership") {
    const collectionIds = (
      expression as { collectionIds?: (string | undefined)[] }
    ).collectionIds;
    return (
      collectionIds !== undefined &&
      collectionIds.some((id) => id !== undefined)
    );
  }
  if (predicateType === "product_membership") {
    const products = (
      expression as {
        products?: { productId?: string; variantIds?: string[] }[];
      }
    ).products;
    // Check that there are products with productIds
    // variantIds is optional - undefined/empty means "all variants match"
    return (
      products !== undefined &&
      products.length > 0 &&
      products.every((p) => p.productId !== undefined)
    );
  }
  if (predicateType === "product_tags") {
    const tags = (expression as { tags?: (string | undefined)[] }).tags;
    return tags !== undefined && tags.some((tag) => tag !== undefined);
  }
  if (
    predicateType === "cart_product_count" ||
    predicateType === "cart_subtotal"
  ) {
    const comparisonValue = (expression as { comparisonValue?: number })
      .comparisonValue;
    return comparisonValue !== undefined;
  }
  if (predicateType === "discount_code") {
    const matchValues = (expression as { matchValues?: (string | undefined)[] })
      .matchValues;
    return (
      matchValues !== undefined &&
      matchValues.some((v) => v !== undefined && v.length > 0)
    );
  }
  if (predicateType === "discount_percentage") {
    const comparisonValue = (expression as { comparisonValue?: number })
      .comparisonValue;
    return comparisonValue !== undefined;
  }
  return false;
}

const ConditionComponent = memo(function ConditionComponent(
  props: StepTypeDetailsProps<PartialWithRequiredType<ConditionStep>>,
) {
  const { schema, step, setStep, category, onClose, showValidationErrors } =
    props;
  const expression = step.expression;

  return (
    <ConditionExpressionInspector
      category={category}
      expression={expression as DeepPartial<ConditionExpression> | undefined}
      onClose={onClose}
      schema={schema}
      setExpression={(expression) =>
        setStep({
          ...step,
          expression,
        } as PartialWithRequiredType<ConditionStep>)
      }
      showValidationErrors={showValidationErrors}
    />
  );
});

export const conditionStepType: FrontendStepType<
  PartialWithRequiredType<ConditionStep>
> = {
  valid: (state) => {
    return (
      state.expression !== undefined &&
      expressionIsValid(
        state.expression as DeepPartial<ConditionExpression> | undefined,
      ) &&
      state.nextFalseId !== undefined &&
      state.nextTrueId !== undefined
    );
  },
  type: "custom",
  customInspector: ConditionComponent,
};

export const ConditionExpressionInspector = memo(
  function ConditionExpressionInspector({
    expression,
    setExpression,
    schema,
    category,
    onClose,
    showTitle = true,
    showValidationErrors,
  }: {
    expression: DeepPartial<ConditionExpression> | undefined;
    setExpression: (expression: DeepPartial<ConditionExpression>) => void;
    schema: Schema;
    category: Category;
    onClose?: () => void;
    /** This inspector is reused in the trigger step, where the title is not needed */
    showTitle?: boolean;
    showValidationErrors?: boolean;
  }) {
    const [touched, setTouched] = useState(showValidationErrors || false);
    const delayedAutoFocus = useDelayedAutoFocus(!expression?.dataSource);

    const schemaHasCustomerIdField = useMemo(
      () => Object.keys(schema.fields).includes("redoCustomerId"),
      // FIXME
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    const errorText = useMemo(() => {
      if (!expression?.dataSource && touched) {
        return "A data source is required";
      }
      return undefined;
    }, [expression?.dataSource, touched]);

    const conditionDataSourceOptions: RedoListItem<ConditionDataSource>[] =
      useMemo(() => {
        if (category === "Upsell") {
          return [
            {
              id: ConditionDataSource.OfferResponse,
              type: "text",
              text: conditionDataSourceToFriendly[
                ConditionDataSource.OfferResponse
              ],
              value: ConditionDataSource.OfferResponse,
            },
          ];
        } else if (schemaHasCustomerIdField) {
          return Object.values(ConditionDataSource)
            .filter((source) => source !== ConditionDataSource.OfferResponse)
            .map((value) => ({
              id: value,
              type: "text",
              text: conditionDataSourceToFriendly[value],
              value,
            }));
        } else {
          return [
            {
              id: ConditionDataSource.TriggerData,
              type: "text",
              text: conditionDataSourceToFriendly[
                ConditionDataSource.TriggerData
              ],
              value: ConditionDataSource.TriggerData,
            },
          ];
        }
        // FIXME
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [category]);

    const expressionTypeInspector = useMemo(() => {
      switch (expression?.dataSource) {
        case ConditionDataSource.TriggerData:
          return (
            <TriggerDataConditionInspector
              expression={expression.schemaBooleanExpression}
              schema={schema}
              setExpression={(schemaBooleanExpression) =>
                setExpression({
                  ...expression,
                  dataSource: ConditionDataSource.TriggerData,
                  schemaBooleanExpression,
                })
              }
              showValidationErrors={showValidationErrors}
            />
          );
        case ConditionDataSource.ExistingSegment:
          return (
            <SegmentMembershipInspector
              segmentIds={expression.segmentIds}
              setSegmentIds={(segmentIds) =>
                setExpression({
                  ...expression,
                  segmentIds,
                  dataSource: ConditionDataSource.ExistingSegment,
                })
              }
              showValidationErrors={showValidationErrors}
            />
          );
        case ConditionDataSource.InlineSegment:
          return (
            <InlineSegmentConditionInspector
              inlineSegment={
                expression.inlineSegment as SegmentConditionBlockType
              }
              setInlineSegment={(inlineSegment) =>
                setExpression({
                  ...expression,
                  dataSource: ConditionDataSource.InlineSegment,
                  inlineSegment,
                })
              }
            />
          );
        case ConditionDataSource.OfferResponse:
          return null;
        case undefined:
          return null;
        default:
          assertNever(expression);
      }
      // FIXME
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expression, schema]);

    return (
      <Flex dir="column" gap="3xl">
        {showTitle && (
          <Flex justify="space-between" py="sm">
            <Text fontSize="sm" fontWeight="semibold">
              Condition
            </Text>
            {onClose && (
              <div className={inspectorStyles.closeButton} onClick={onClose}>
                <XIcon height="16" width="16" />
              </div>
            )}
          </Flex>
        )}
        <Flex dir="column">
          <RedoSingleSelectDropdownInput
            autoFocus={delayedAutoFocus}
            description={errorText}
            key={delayedAutoFocus.toString()}
            label="Data source"
            onBlur={() => setTouched(true)}
            options={conditionDataSourceOptions}
            optionSelected={({ item }) => {
              setExpression({ dataSource: item.value });
            }}
            placeholder="Select a data source"
            selectedItem={conditionDataSourceOptions.find(
              (o) => o.id === expression?.dataSource,
            )}
            size="xs"
            state={errorText ? "error" : "default"}
          />
          {expressionTypeInspector}
        </Flex>
      </Flex>
    );
  },
);

const segmentSearcher = new Fuse([], { keys: ["value"] });

const SegmentMembershipInspector = memo(function SegmentMembershipInspector({
  segmentIds,
  setSegmentIds,
  showValidationErrors,
}: {
  segmentIds: (string | undefined)[] | undefined;
  setSegmentIds: (segmentIds: string[]) => void;
  showValidationErrors?: boolean;
}) {
  const [segments] = useLazyContext(SegmentsContext);

  const [searchString, setSearchString] = useState<string>("");
  const [touched, setTouched] = useState(showValidationErrors || false);
  const delayedAutoFocus = useDelayedAutoFocus(
    !segmentIds || !segmentIds.length,
  );

  const segmentOptions: BadgeRedoListItem<MinimalCustomerGroup>[] =
    useMemo(() => {
      if (!segments.value) return [];
      return segments.value.map<BadgeRedoListItem<MinimalCustomerGroup>>(
        (segment) => ({
          id: segment._id,
          value: segment,
          type: "badge",
          badge: { text: segment.name },
        }),
      );
    }, [segments]);

  const selectedSegmentOptions = useMemo(() => {
    const ids = filterTruthy(segmentIds ?? []);
    return filterTruthy(
      ids.map((id) => segmentOptions.find((option) => option.id === id)),
    );
  }, [segmentIds, segmentOptions]);

  const searchResults = useSearch(
    segmentSearcher,
    segmentOptions,
    searchString,
  );

  const errorText = useMemo(() => {
    if ((!segmentIds || segmentIds.length === 0) && touched) {
      return "At least one segment is required";
    }
    return undefined;
  }, [segmentIds, touched]);

  return (
    <RedoTagsDropdownInput
      autoFocus={delayedAutoFocus}
      description={errorText}
      fitToAnchor
      key={delayedAutoFocus.toString()}
      loading={segments.pending}
      onBlur={() => setTouched(true)}
      options={searchResults}
      placeholder="Select segments"
      searchPlaceholder="Search segments"
      searchString={searchString}
      selectedOptions={selectedSegmentOptions}
      setSearchString={setSearchString}
      setSelectedOptions={(options) => {
        setSegmentIds(options.map((option) => option.value._id));
      }}
      size="xs"
      state={errorText ? "error" : "default"}
    />
  );
});

const InlineSegmentConditionInspector = memo(
  function InlineSegmentConditionInspector({
    inlineSegment,
    setInlineSegment,
  }: {
    inlineSegment: SegmentConditionBlockType | undefined;
    setInlineSegment: (expression: SegmentConditionBlockType) => void;
  }) {
    // Fetch enabled integrations for this team (for order-placed activities)
    const integrations = useIntegrationsByTeam();
    // Fetch marketing-enabled integrations (for other CommentSold activities)
    const marketingProviders = useTeamMarketingProviders();
    // Fetch enabled integrations for filtering segment characteristics
    const enabledIntegrations = useEnabledIntegrations();

    // Convert to CustomerDataSource[] for filtering
    const availableDataSourcesForOrders = useMemo(() => {
      return providersToDataSources(integrations.data ?? []);
    }, [integrations.data]);

    const availableDataSourcesForMarketing = useMemo(() => {
      return providersToDataSources(marketingProviders.data?.available ?? []);
    }, [marketingProviders.data?.available]);

    const block = useMemo(
      () => inlineSegment ?? { mode: ConjunctionMode.AND, conditions: [] },
      [inlineSegment],
    );

    const isValid = useMemo(() => {
      return isSegmentConditionBlockValid(block);
    }, [block]);

    const hasConditions = block.conditions && block.conditions.length > 0;

    return (
      <Flex dir="column" gap="sm">
        <SegmentBuilderLocationContext.Provider
          value={SegmentEditorLocation.AUTOMATIONS_PANEL}
        >
          <SegmentConditionBlock
            availableDataSourcesForMarketing={availableDataSourcesForMarketing}
            availableDataSourcesForOrders={availableDataSourcesForOrders}
            block={block}
            enabledIntegrations={enabledIntegrations}
            setBlock={(block) => {
              setInlineSegment(block as SegmentConditionBlockType);
            }}
            state={!isValid ? "error" : "default"}
          />
        </SegmentBuilderLocationContext.Provider>
        {!isValid && (
          <Text fontSize="xs" textColor="error">
            {!hasConditions
              ? "At least one condition is required"
              : "Please complete all condition fields"}
          </Text>
        )}
      </Flex>
    );
  },
);

const supportedVariableTypes: GenericDataType[] = [
  "Boolean",
  "Text",
  "Enum",
  "Token",
  "Number",
  "Checkout Context Predicate",
];
const supportedGenericTypes = ["Multiple", "Maybe Text", "Maybe Number"];

const TriggerDataConditionInspector = memo(
  function TriggerDataConditionInspector({
    expression,
    setExpression,
    schema,
    showValidationErrors,
  }: {
    expression: DeepPartial<SchemaBooleanExpression> | undefined;
    setExpression: (expression: DeepPartial<SchemaBooleanExpression>) => void;
    schema: Schema;
    showValidationErrors?: boolean;
  }) {
    const [touched, setTouched] = useState(showValidationErrors || false);
    const delayedAutoFocus = useDelayedAutoFocus(!expression?.field);

    const errorText = useMemo(() => {
      if (!expression?.field && touched) {
        return "A variable is required";
      }
      return undefined;
    }, [expression?.field, touched]);

    const variableNames: RedoListItem<string>[] = useMemo(() => {
      const rawNames = Object.keys(schema.fields)
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        .filter((key) => !schema.fields[key].hideFromConditionStep)
        .filter(
          (key) =>
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            supportedVariableTypes.includes(schema.fields[key].dataType) ||
            supportedGenericTypes.some((type) =>
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              schema.fields[key].dataType.startsWith(type),
            ),
        );

      return rawNames.map((key) => ({
        id: key,
        type: "text",
        text: camelToTitleCase(key),
        value: key,
      }));
    }, [schema.fields]);

    const selectedFieldName = expression?.field?.toString();
    const selectedFieldType = selectedFieldName
      ? schema.fields[selectedFieldName]?.dataType
      : undefined;
    const selectedField = selectedFieldName
      ? schema.fields[selectedFieldName]
      : undefined;

    const expressionWithDefault = useMemo(() => expression ?? {}, [expression]);

    return (
      <>
        <RedoSingleSelectDropdownInput
          autoFocus={delayedAutoFocus}
          description={errorText}
          key={delayedAutoFocus.toString()}
          label="Variable"
          onBlur={() => setTouched(true)}
          options={variableNames}
          optionSelected={({ item }) => {
            setExpression({ field: item.value });
          }}
          placeholder="Select a variable"
          selectedItem={variableNames.find(
            (o) => o.id === expression?.field?.toString(),
          )}
          size="xs"
          state={errorText ? "error" : "default"}
        />
        {selectedFieldName && selectedFieldType && selectedField && (
          <FieldInspector
            dataType={selectedFieldType}
            expression={expressionWithDefault}
            schema={schema}
            selectedField={selectedField}
            setExpression={(expression) => {
              setExpression(expression);
            }}
            showValidationErrors={showValidationErrors}
          />
        )}
      </>
    );
  },
);

export function expressionIsValid(
  expression?: DeepPartial<ConditionExpression>,
): boolean {
  if (!expression) return false;

  switch (expression.dataSource) {
    case ConditionDataSource.TriggerData:
      return triggerDataExpressionIsValid(expression.schemaBooleanExpression);
    case ConditionDataSource.ExistingSegment:
      return (
        expression.segmentIds !== undefined && expression.segmentIds.length > 0
      );
    case ConditionDataSource.InlineSegment:
      return (
        !!expression &&
        !!expression.inlineSegment &&
        isSegmentConditionBlockValid(
          expression.inlineSegment as SegmentConditionBlockType,
        )
      );
    case ConditionDataSource.OfferResponse:
      return true;
    case undefined:
      return false;
    default:
      assertNever(expression);
  }
}

function triggerDataExpressionIsValid(
  expression?: DeepPartial<SchemaBooleanExpression>,
): boolean {
  if (!expression) return false;
  switch (expression.type) {
    case "number_comparison":
      return (
        expression.comparisonValue !== undefined &&
        expression.field !== undefined &&
        expression.operator !== undefined
      );
    case "text_match":
      return (
        expression.field !== undefined &&
        expression.operator !== undefined &&
        expression.matchValues !== undefined &&
        expression.matchValues.length > 0
      );
    case "array_predicate":
      return (
        expression.field !== undefined &&
        expression.operator !== undefined &&
        expression.condition !== undefined &&
        triggerDataExpressionIsValid(expression.condition)
      );
    case "boolean_evaluation":
      return expression.field !== undefined;
    case "checkout_context_predicate":
      return (
        expression.field !== undefined &&
        expression.predicateType !== undefined &&
        expression.operator !== undefined &&
        checkoutContextPredicateHasValues(expression)
      );
    case undefined:
      return false;
    default:
      assertNever(expression);
  }
}
